import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    LiveKitRoom,
    RoomAudioRenderer,
    useVoiceAssistant,
    BarVisualizer,
    VoiceAssistantControlBar,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { baseUrl, voiceBaseUrl } from '../config/apiConfig';
import { getDataFromLocalStorage } from '../util/localStorage';
import './VoiceCall.css';

const LIVEKIT_URL = 'wss://lb-test-t5zay2mp.livekit.cloud';

// Fetch a token from DocLift-API, passing col_id so the agent queries the right collection
async function generateToken(roomName, identity, colId) {
    let url = `${baseUrl}/users/livekit-token?room=${encodeURIComponent(roomName)}&identity=${encodeURIComponent(identity)}`;
    if (colId) url += `&col_id=${encodeURIComponent(colId)}`;
    const token = getDataFromLocalStorage('token');

    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    if (!resp.ok) {
        if (resp.status === 401) throw new Error('Unauthorized: Please log in again');
        throw new Error(`Token server error: ${resp.status}`);
    }
    const data = await resp.json();
    return data.token;
}

// Readable labels for each agent state string value
const STATE_LABELS = {
    'disconnected': 'Disconnected',
    'connecting': 'Connecting...',
    'pre-connect-buffering': 'Buffering...',
    'failed': 'Connection failed',
    'idle': 'Waiting...',
    'listening': 'Listening...',
    'thinking': 'Thinking...',
    'speaking': 'Speaking...',
};

function AgentVisualizer() {
    const { state, audioTrack } = useVoiceAssistant();
    const label = STATE_LABELS[state] ?? state ?? 'Ready';

    return (
        <div className="agent-area">
            <div className={`agent-orb agent-orb--${state ?? 'idle'}`}>
                <BarVisualizer
                    state={state}
                    barCount={5}
                    trackRef={audioTrack}
                    className="bar-viz"
                    options={{ minHeight: 4 }}
                />
            </div>
            <p className="agent-state-label text-slate-700">{label}</p>
            <VoiceAssistantControlBar className="control-bar" />
        </div>
    );
}

function CallRoom({ token, onDisconnect }) {
    return (
        <LiveKitRoom
            token={token}
            serverUrl={LIVEKIT_URL}
            connect={true}
            audio={true}
            video={false}
            onDisconnected={onDisconnect}
            className="lk-room-container"
        >
            <RoomAudioRenderer />
            <AgentVisualizer />
        </LiveKitRoom>
    );
}

export default function VoiceCall() {
    const [searchParams] = useSearchParams();
    const colId = searchParams.get('col_id');   // passed from MyDocs: /voice-call?col_id=5

    const [status, setStatus] = useState('idle'); // idle | connecting | connected | error
    const [token, setToken] = useState(null);
    const [error, setError] = useState(null);

    const handleCall = useCallback(async () => {
        setStatus('connecting');
        setError(null);
        try {
            const roomName = `room-${Date.now()}`;
            const identity = `user-${Math.random().toString(36).slice(2, 7)}`;
            const t = await generateToken(roomName, identity, colId);   // pass colId
            setToken(t);
            setStatus('connected');
        } catch (err) {
            setError(err.message || 'Failed to connect');
            setStatus('error');
        }
    }, [colId]);

    const handleDisconnect = useCallback(() => {
        setToken(null);
        setStatus('idle');
    }, []);

    return (
        <div className="flex min-h-screen bg-[#f3f6fa] pt-[60px] justify-center items-center voice-call-wrapper relative overflow-hidden">
            {/* Background train-themed accents (e.g. tracks, subtle gradients) */}
            <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-[#013480]/5 rounded-bl-full pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-[#e37e0e]/5 rounded-tr-full pointer-events-none"></div>

            <div className="glass-card shadow-[0_10px_40px_rgba(1,52,128,0.1)] rounded-3xl p-8 max-w-lg w-full text-center bg-white border-t-4 border-t-[#013480] relative z-10">
                <div className="logo-wrap flex flex-col items-center mb-6">
                    <div className="logo-icon mb-4 bg-[#f0f4f8] p-4 rounded-full shadow-inner">
                        {/* Train / IRCTC style Icon */}
                        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="60" height="60">
                            <path d="M24 2C14.059 2 6 9.4 6 18V36C6 39.314 8.686 42 12 42H36C39.314 42 42 39.314 42 36V18C42 9.4 33.941 2 24 2Z" fill="#013480" />
                            <path d="M14 26H34V32H14V26Z" fill="#ff9933" />
                            <path d="M18 10C18 7.79 19.79 6 22 6H26C28.21 6 30 7.79 30 10V18C30 20.21 28.21 22 26 22H22C19.79 22 18 20.21 18 18V10Z" fill="#ffffff" />
                            <circle cx="15" cy="38" r="3" fill="#ffffff" />
                            <circle cx="33" cy="38" r="3" fill="#ffffff" />
                            <path d="M20 42L16 48M28 42L32 48" stroke="#013480" strokeWidth="4" strokeLinecap="round" />
                        </svg>
                    </div>
                    <h1 className="title text-2xl font-black text-[#013480] uppercase tracking-wide">Indian Railways</h1>
                    <h2 className="text-lg font-bold text-[#e37e0e] mt-1">Disha AI Tech Support</h2>
                </div>

                <p className="subtitle text-gray-600 mb-4 px-2 font-medium">
                    AI-powered technical assistance and troubleshooting for Loco Pilots and train operations.
                </p>

                {/* Show which KB collection is active */}
                {colId && (
                    <div className="mb-6 inline-flex items-center gap-2 bg-[#f0f4f8] border border-[#013480]/20 text-[#013480] text-xs font-semibold px-3 py-1.5 rounded-full">
                        <span>📚</span>
                        <span>Knowledge Base: Collection #{colId}</span>
                    </div>
                )}

                {status !== 'connected' && (
                    <div className="call-section">
                        <button
                            id="call-button"
                            className={`call-button flex items-center justify-center gap-3 w-full max-w-xs mx-auto py-3.5 rounded-full font-bold text-white transition shadow-lg border-2 ${status === 'connecting' ? 'bg-gray-400 border-gray-400 cursor-not-allowed' : 'bg-[#013480] border-[#013480] hover:bg-white hover:text-[#013480]'
                                }`}
                            onClick={handleCall}
                            disabled={status === 'connecting'}
                        >
                            {status === 'connecting' ? (
                                <>
                                    <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    Dialing...
                                </>
                            ) : (
                                <>
                                    <svg viewBox="0 0 24 24" fill="none" width="22" height="22" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8a19.79 19.79 0 01-3.07-8.66A2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92z" fill="currentColor" />
                                    </svg>
                                    Call Ask Disha AI
                                </>
                            )}
                        </button>

                        {error && (
                            <div className="error-msg mt-4 text-[#e3000f] bg-[#e3000f]/10 text-sm py-2 px-4 rounded-xl inline-block font-semibold">
                                {error}
                            </div>
                        )}
                    </div>
                )}

                {status === 'connected' && token && (
                    <div className="room-section mt-4 relative bg-[#013480]/5 p-6 rounded-2xl border border-[#013480]/10">
                        <CallRoom token={token} onDisconnect={handleDisconnect} />
                        <button
                            id="hangup-button"
                            className="bg-[#e3000f] hover:bg-red-700 text-white font-bold py-2.5 px-8 rounded-full mt-6 shadow-md transition"
                            onClick={handleDisconnect}
                        >
                            Disconnect
                        </button>
                    </div>
                )}

                <div className="status-dot-row flex items-center justify-center gap-2 mt-8 text-sm font-semibold text-gray-500">
                    <span className={`w-3 h-3 rounded-full ${status === 'connected' ? 'bg-[#3bb824] shadow-[0_0_8px_rgba(59,184,36,0.6)]'
                        : status === 'connecting' ? 'bg-[#ff9933] shadow-[0_0_8px_rgba(255,153,51,0.6)]'
                            : status === 'error' ? 'bg-[#e3000f]'
                                : 'bg-gray-400'
                        }`} />
                    <span>
                        {status === 'connected' ? 'On Call with Disha' : status === 'connecting' ? 'Connecting to Servers...' : status === 'error' ? 'System Error' : 'Servers Online'}
                    </span>
                </div>
            </div>
        </div>
    );
}
